import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewContainerRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Reminder } from '@app/core/types/reminder';
import { ReminderEditComponent } from '@app/main/credit/credit-edit/credit-communication/reminder-edit/reminder-edit.component';
import { Credit } from '@app/core/types/credit';
import { Subject, filter, takeUntil } from 'rxjs';
import { ReminderService } from '@app/core/services/reminder.service';
import { SearchDirection, SearchOptions } from '@app/core/types/search-options';
import { Page } from '@app/core/types/page';
import { SearchOperations } from '@app/core/types/search-criterium';
import { DialogProviderService } from '@app/core/services/dialog-provider.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@app/core/services/notification.service';

@Component({
  selector: 'itfg-reminder',
  templateUrl: './reminder.component.html',
  styleUrls: ['./reminder.component.scss'],
  standalone: false,
})
export class ReminderComponent implements OnChanges, OnInit, OnDestroy {
  @Input() openedCredit: Credit;

  reminders: Reminder[] = [];
  expandedReminderId: number | null = null;

  private _unsubscribe = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private reminderService: ReminderService,
    private dialogProviderService: DialogProviderService,
    private translate: TranslateService,
    private viewContainerRef: ViewContainerRef,
    private notifications: NotificationService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.openedCredit) {
      this.expandedReminderId = null;
      this.reminders = [];
      this.getReminder();
    }
  }

  ngOnInit() {
    this.reminderService.refreshReminder$
      .pipe(takeUntil(this._unsubscribe))
      .subscribe(() => {
        this.getReminder();
      });
  }

  openReminderDialog(reminder: Reminder = null) {
    this.dialog.open(ReminderEditComponent, {
      data: {
        reminder,
        credit: this.openedCredit,
      },
      minWidth: '400px',
      restoreFocus: false,
    });
  }

  getReminder() {
    if (!this.openedCredit) {
      return;
    }

    const creditId = this.openedCredit.id;
    const searchOptions = new SearchOptions({
      page: 0,
      pageSize: 5,
      column: 'time',
      direction: SearchDirection.ASCENDING,
      criteria: [
        {
          key: 'creditId',
          operation: SearchOperations.EQUALS,
          value: creditId,
        },
        {
          key: 'time',
          operation: SearchOperations.GREATER_THAN,
          value: new Date().toISOString(),
        },
      ],
    });

    this.reminderService
      .getReminderList$(searchOptions.getDTO())
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: (page: Page<Reminder>) => {
          if (this.openedCredit?.id === creditId) {
            this.reminders = page.content;
          }
        },
        error: (error) => {
          console.error('Error fetching reminders:', error);
        },
      });
  }

  deactivateReminder(reminder: Reminder) {
    this.dialogProviderService
      .openConfirm({
        message: this.translate.instant(
          'communication.deactivateReminderMessage'
        ),
        disableClose: false,
        viewContainerRef: this.viewContainerRef,
        title: this.translate.instant('global.deactivate'),
        cancelButton: this.translate.instant('global.cancel'),
        acceptButton: this.translate.instant('global.confirm'),
      })
      .afterClosed()
      .subscribe((accept: boolean) => {
        if (accept) {
          this.reminderService
            .deactivateReminder(reminder.id)
            .subscribe((res) => {
              this.notifications.showLocalizedSuccessMessage({
                notificationText:
                  'communication.deactivateReminderSuccessMessage',
              });
              this.getReminder();
            });
        }
      });
  }

  ngOnDestroy() {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}
